import { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  MessageCircle,
  MousePointer2,
  TrendingUp,
  Zap,
} from "lucide-react";

// ---------------------------------------------------------------
// GuidedDashboard: interactive hero product preview.
// Local demo data only. No backend, no auth, no API keys.
// Transitions are CSS-only (transform/opacity, <=300ms, ease-out).
// ---------------------------------------------------------------

const STEP_DEFS = [
  {
    id: "enquiries",
    label: "Enquiries",
    eyebrow: "New parent enquiries",
    title: "Every enquiry, captured",
    description: "New WhatsApp and web leads land in one organised view.",
    activeMetric: 0,
  },
  {
    id: "follow-ups",
    label: "Follow-ups",
    eyebrow: "Today's follow-ups",
    title: "Nothing slips through",
    description: "Your team sees exactly who needs a response and why.",
    activeMetric: 1,
  },
  {
    id: "trials",
    label: "Trials",
    eyebrow: "Upcoming trials",
    title: "Trials stay on track",
    description: "See confirmed bookings, attendance and next actions.",
    activeMetric: 2,
  },
];

const INITIAL_ENQUIRY_ITEMS = [
  { name: "Mdm Tan · Emma", detail: "Primary 3 English", status: "Just now", more: "Source: WhatsApp · Prefers Tue 4:30 pm · Bukit Timah branch" },
  { name: "Mr Rahman · Isaac", detail: "P5 Maths enquiry", status: "2m ago", more: "Source: Website form · Prefers Sat morning · Tampines branch" },
  { name: "Ms Hassan · Aarav", detail: "Trial class requested", status: "4m ago", more: "Source: WhatsApp · Prefers Wed 5:00 pm · Punggol branch" },
];

const FOLLOW_UP_ITEMS = [
  { name: "Mdm Tan · Emma", detail: "Trial attended, no decision yet", status: "Today", more: "Teacher feedback sent · Decision call scheduled 3 pm" },
  { name: "Ms Hassan · Aarav", detail: "Send programme details", status: "Today", more: "Requested P4 Science syllabus · WhatsApp preferred" },
  { name: "Mr Rahman · Isaac", detail: "Check preferred timeslot", status: "Today", more: "Two slots held: Sat 10 am or Wed 5 pm · Expires tomorrow" },
];

const TRIAL_ITEMS = [
  { name: "Emma Tan", detail: "English · Tue 4:30 pm", status: "Confirmed", more: "Reminder sent at T-24h · Teacher: Ms Chong" },
  { name: "Aarav Hassan", detail: "Maths · Wed 5:00 pm", status: "Confirmed", more: "Parent confirmed on WhatsApp · Seat 6 of 8" },
  { name: "Isaac Rahman", detail: "Science · Sat 10:00 am", status: "Pending", more: "Awaiting parent confirmation · Hold expires in 18h" },
];

const INITIAL_METRICS = [
  { id: "new-enquiries", label: "New enquiries", value: "128", change: "+12%", icon: MessageCircle, bars: [35, 55, 42, 72, 58, 78, 68, 90] },
  { id: "median-response", label: "Median response time", value: "2m", change: "On target", icon: Clock3, bars: [80, 62, 70, 48, 55, 40, 36, 30] },
  { id: "trials-booked", label: "Trials booked", value: "41", change: "+8", icon: CalendarDays, bars: [30, 44, 38, 52, 60, 56, 70, 82] },
  { id: "conversion", label: "Enquiry-to-enrolment", value: "34%", change: "+3 pts", icon: TrendingUp, bars: [28, 34, 40, 38, 46, 52, 58, 64] },
];

const INITIAL_PIPELINE = [
  { id: "new", label: "New enquiry", count: 24 },
  { id: "contacted", label: "Contacted", count: 18 },
  { id: "trial", label: "Trial booked", count: 12 },
  { id: "enrolled", label: "Enrolled", count: 9 },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

export const GuidedDashboard = () => {
  const reduceMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusIndex, setFocusIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [enquiryItems, setEnquiryItems] = useState(INITIAL_ENQUIRY_ITEMS);
  const [metrics, setMetrics] = useState(INITIAL_METRICS);
  const [pipeline, setPipeline] = useState(INITIAL_PIPELINE);
  const [highlightStage, setHighlightStage] = useState(null);
  const [automationDone, setAutomationDone] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const tabRefs = useRef([]);
  const highlightTimer = useRef(null);

  const steps = useMemo(
    () =>
      STEP_DEFS.map((step) => ({
        ...step,
        items: step.id === "enquiries" ? enquiryItems : step.id === "follow-ups" ? FOLLOW_UP_ITEMS : TRIAL_ITEMS,
      })),
    [enquiryItems]
  );
  const activeStep = steps[activeIndex];

  // Auto-advance (~4.2s). Pauses permanently on first user interaction.
  // Disabled entirely under prefers-reduced-motion.
  useEffect(() => {
    if (isPaused || reduceMotion) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % STEP_DEFS.length);
      setExpandedItem(null);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [isPaused, reduceMotion]);

  useEffect(() => () => window.clearTimeout(highlightTimer.current), []);

  const pause = () => setIsPaused(true);

  const activateStep = (index, viaUser) => {
    setActiveIndex(index);
    setFocusIndex(index);
    setExpandedItem(null);
    if (viaUser) {
      pause();
      setAnnouncement(`${STEP_DEFS[index].label} step selected. ${STEP_DEFS[index].title}.`);
    }
  };

  const onTabKeyDown = (event, index) => {
    let next = null;
    if (event.key === "ArrowRight") next = (index + 1) % STEP_DEFS.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + STEP_DEFS.length) % STEP_DEFS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = STEP_DEFS.length - 1;
    else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      activateStep(index, true);
      return;
    }
    if (next !== null) {
      event.preventDefault();
      pause();
      setFocusIndex(next);
      tabRefs.current[next]?.focus();
    }
  };

  const toggleItem = (name) => {
    pause();
    setExpandedItem((current) => {
      const nextVal = current === name ? null : name;
      if (nextVal) setAnnouncement(`Showing details for ${name}.`);
      return nextVal;
    });
  };

  const runAutomation = () => {
    if (automationDone) return;
    pause();
    setAutomationDone(true);
    setActiveIndex(0);
    setFocusIndex(0);
    setExpandedItem(null);
    setEnquiryItems((items) => [
      { name: "Mrs Lee · Kayden", detail: "P4 Science enquiry", status: "Auto-replied", more: "Auto-reply sent in 12s · Teacher-approved trial slot offered · Moved to Trial booked" },
      ...items,
    ]);
    setMetrics((rows) =>
      rows.map((m) => {
        if (m.id === "new-enquiries") return { ...m, value: "129", change: "+13%" };
        if (m.id === "trials-booked") return { ...m, value: "42", change: "+9" };
        if (m.id === "conversion") return { ...m, value: "35%", change: "+4 pts" };
        return m;
      })
    );
    setPipeline((stages) => stages.map((s) => (s.id === "trial" ? { ...s, count: s.count + 1 } : s)));
    if (!reduceMotion) {
      setHighlightStage("trial");
      highlightTimer.current = window.setTimeout(() => setHighlightStage(null), 400);
    }
    setAnnouncement("New enquiry from Mrs Lee received an automatic reply and moved to Trial booked. Trials booked is now 42.");
  };

  return (
    <section aria-label="Interactive EnquiryOps product preview" className="relative" data-testid="guided-dashboard">
      <p aria-live="polite" role="status" className="sr-only" data-testid="guided-dashboard-announcer">{announcement}</p>

      {/* Header row */}
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-[var(--ink)]">See how it works</p>
          <p className="mt-1 text-sm font-medium text-[var(--muted-ink)]">Follow an enquiry from first message to enrolment.</p>
        </div>
        <span className="hidden shrink-0 rounded-full border border-[var(--line)] px-2.5 py-1 text-[10px] font-semibold text-[var(--muted-ink)] sm:inline-flex" data-testid="guided-demo-data-chip">
          Demo data
        </span>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Dashboard tour steps" className="mb-3 grid grid-cols-3 gap-2">
        {steps.map((step, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={step.id}
              id={`tour-tab-${step.id}`}
              ref={(el) => (tabRefs.current[index] = el)}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="tour-panel"
              tabIndex={focusIndex === index ? 0 : -1}
              onClick={() => activateStep(index, true)}
              onKeyDown={(e) => onTabKeyDown(e, index)}
              data-testid={`guided-tab-${step.id}`}
              className={`relative min-h-[44px] overflow-hidden rounded-xl border px-2 py-2 text-center text-[11px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 sm:px-3 sm:text-xs ${
                isActive
                  ? "border-[var(--brand)] bg-[var(--surface-2)] text-[var(--navy-900)] shadow-[var(--shadow-sm)]"
                  : "border-[var(--line)] bg-[var(--surface-1)] text-[var(--muted-ink)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)] active:bg-[var(--surface-2)]"
              }`}
            >
              <span className="relative z-10">{step.label}</span>
              {isActive && <span aria-hidden="true" className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-[var(--brand)]" />}
            </button>
          );
        })}
      </div>

      {/* Dashboard frame: no max-height on mobile (content stacks taller), capped on md+ */}
      <div className="w-full max-w-full min-w-0 md:max-h-[540px] overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface-1)] shadow-[var(--shadow-lg)]">
        <div
          id="tour-panel"
          role="tabpanel"
          aria-labelledby={`tour-tab-${activeStep.id}`}
          className="relative min-h-[380px] overflow-hidden bg-[var(--surface-2)] sm:min-h-[400px]"
        >
          {/* Overview header (padded clear of the step aside) */}
          <div className="border-b border-[var(--line)] bg-[var(--surface-1)] py-2 pl-4 pr-[48%] sm:pl-5 sm:pr-[46%]">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] leading-tight text-[var(--muted-ink)]">Centre overview</p>
                <h3 className="truncate text-sm font-bold leading-tight text-[var(--ink)]">Good morning, Alicia</h3>
              </div>
              <span className="hidden shrink-0 rounded-lg border border-[var(--line)] bg-[var(--surface-1)] px-2.5 py-1 text-[10px] font-bold text-[var(--muted-ink)] md:inline">
                Last 30 days
              </span>
            </div>
          </div>

          {/* KPI grid (left of the step aside) */}
          <div className="grid grid-cols-2 gap-2 p-2.5 pr-[48%] sm:gap-3 sm:p-3 sm:pr-[46%]">
            {metrics.map((metric, index) => {
              const MetricIcon = metric.icon;
              const isActive = index === activeStep.activeMetric;
              return (
                <article
                  key={metric.id}
                  data-testid={`guided-kpi-${metric.id}`}
                  className={`relative min-h-24 rounded-xl border p-2.5 transition-opacity duration-300 ease-out sm:p-3 ${
                    isActive
                      ? "border-[var(--brand)] bg-[var(--surface-2)] opacity-100 shadow-[var(--shadow-sm)]"
                      : "border-[var(--line)] bg-[var(--surface-1)] opacity-60"
                  }`}
                >
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="gd-pop-in absolute -right-2 -top-2 z-10 hidden h-7 w-7 items-center justify-center rounded-full border-2 border-[var(--paper)] bg-[var(--brand)] text-[var(--paper)] shadow-[var(--shadow-sm)] sm:flex"
                    >
                      <MousePointer2 className="h-3.5 w-3.5" fill="currentColor" />
                    </span>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[9px] font-bold uppercase tracking-wide sm:text-[10px] ${isActive ? "text-[var(--navy-900)]" : "text-[var(--muted-ink)]"}`}>
                      {metric.label}
                    </span>
                    <MetricIcon
                      aria-hidden="true"
                      className={`h-3.5 w-3.5 shrink-0 ${metric.id === "conversion" ? "hidden md:block" : ""} ${isActive ? "text-[var(--brand)]" : "text-[var(--muted-ink)]"}`}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-1">
                    <strong className={`font-heading text-xl font-semibold tracking-tight sm:text-2xl ${isActive ? "text-[var(--navy-950)]" : "text-[var(--ink)]"}`}>
                      {metric.value}
                    </strong>
                    <span className={`mb-0.5 text-[9px] font-bold text-[var(--muted-ink)] sm:text-[10px]`}>
                      {metric.change}
                    </span>
                  </div>
                  <div className="mt-2 flex h-3.5 items-end gap-0.5" aria-hidden="true">
                    {metric.bars.map((height, barIndex) => (
                      <span
                        key={`${metric.id}-${barIndex}`}
                        className={`flex-1 rounded-sm transition-colors duration-300 ${isActive ? "bg-[var(--brand)]" : "bg-[var(--line)]"}`}
                        style={{ height: `${height}%` }}
                      />
                    ))}
                  </div>
                </article>
              );
            })}

            {/* Compact pipeline row */}
            <div className="col-span-2 rounded-xl border border-[var(--line)] bg-[var(--surface-1)] p-2.5" data-testid="guided-pipeline-row">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[9px] font-bold uppercase tracking-wide text-[var(--muted-ink)] sm:text-[10px]">Pipeline</p>
                <button
                  type="button"
                  onClick={runAutomation}
                  disabled={automationDone}
                  data-testid="guided-run-automation-button"
                  className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3 text-[10px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 sm:min-h-[36px] sm:text-[11px] ${
                    automationDone
                      ? "cursor-default border-[var(--line)] bg-[var(--surface-2)] text-[var(--muted-ink)]"
                      : "border-[var(--line)] bg-[var(--surface-1)] text-[var(--navy-900)] hover:bg-[var(--surface-2)] active:bg-[var(--surface-2)]"
                  }`}
                >
                  {automationDone ? <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" /> : <Zap aria-hidden="true" className="h-3.5 w-3.5" />}
                  {automationDone ? "Automation complete" : "Run automation"}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-y-2">
                {pipeline.map((stage, index) => (
                  <div key={stage.id} className="flex items-center">
                    <div
                      data-testid={`guided-pipeline-${stage.id}`}
                      className={`rounded-lg border px-2 py-1.5 transition-colors duration-300 ${
                        highlightStage === stage.id
                          ? "border-[var(--brand)] bg-[var(--surface-2)]"
                          : "border-[var(--line)] bg-[var(--surface-1)]"
                      }`}
                    >
                      <p className="text-[9px] font-semibold leading-tight text-[var(--muted-ink)]">{stage.label}</p>
                      <p className="font-heading text-sm font-semibold leading-tight text-[var(--ink)]">{stage.count}</p>
                    </div>
                    {index < pipeline.length - 1 && (
                      <ChevronRight aria-hidden="true" className="mx-0.5 h-3 w-3 shrink-0 text-[var(--muted-ink)]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Step aside */}
          <aside
            key={activeStep.id}
            className="gd-slide-in absolute inset-y-0 right-0 z-20 w-[46%] border-l border-[var(--line)] bg-[var(--surface-1)] p-3 shadow-[var(--shadow-md)] sm:w-[44%] sm:p-4"
          >
            <div className="flex h-full flex-col">
              <div className="flex items-start justify-between gap-2 border-b border-[var(--line)] pb-2">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[var(--navy-900)] sm:text-[10px]">{activeStep.eyebrow}</p>
                  <h4 className="mt-1 text-sm font-bold leading-tight text-[var(--navy-950)] sm:text-base">{activeStep.title}</h4>
                </div>
                <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--surface-2)] text-[var(--navy-900)] sm:flex">
                  <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                </span>
              </div>
              <p className="mt-2 hidden text-[11px] font-medium leading-relaxed text-[var(--muted-ink)] sm:block">{activeStep.description}</p>
              <div className="mt-2 space-y-2 overflow-y-auto sm:mt-3">
                {activeStep.items.map((item, index) => {
                  const isExpanded = expandedItem === item.name;
                  return (
                    <div
                      key={item.name}
                      className="gd-rise-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleItem(item.name)}
                        aria-expanded={isExpanded}
                        aria-controls={`guided-item-detail-${activeStep.id}-${index}`}
                        data-testid={`guided-item-${activeStep.id}-${index}`}
                        className={`w-full min-h-[44px] rounded-xl border p-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-1 ${
                          isExpanded
                            ? "border-[var(--brand)] bg-[var(--surface-2)]"
                            : "border-[var(--line)] bg-[var(--surface-2)] hover:border-[var(--brand)] active:border-[var(--brand)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <strong className="block truncate text-[10px] font-bold text-[var(--navy-950)] sm:text-xs">{item.name}</strong>
                            <span className="mt-1 block text-[9px] font-medium leading-snug text-[var(--muted-ink)] sm:text-[10px]">{item.detail}</span>
                          </div>
                          <span className="shrink-0 rounded-full bg-[var(--surface-1)] px-1.5 py-0.5 text-[8px] font-bold text-[var(--navy-900)] sm:px-2 sm:text-[9px]">
                            {item.status}
                          </span>
                        </div>
                        <span
                          id={`guided-item-detail-${activeStep.id}-${index}`}
                          className={`mt-1.5 block border-t border-[var(--line)] pt-1.5 text-[9px] font-medium leading-snug text-[var(--muted-ink)] sm:text-[10px] ${isExpanded ? "" : "hidden"}`}
                        >
                          {item.more}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={pause}
                data-testid="guided-open-step-button"
                className="mt-auto inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-[var(--navy-950)] px-3 py-2.5 text-[10px] font-bold text-[var(--paper)] transition-colors hover:bg-[var(--navy-900)] active:bg-[var(--navy-900)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 sm:text-xs"
              >
                Open {activeStep.label.toLowerCase()}
              </button>
            </div>
          </aside>
        </div>
      </div>

      <p className="mt-3 text-center text-[11px] font-medium text-[var(--muted-ink)] sm:text-left">
        Every enquiry answered. Every follow-up tracked. More trials converted.
      </p>
    </section>
  );
};
